import React, { ChangeEvent, useEffect, useState } from 'react';
import Dialog from '../common/Dialog';
import DialogList from 'src/dialogs';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import actions from 'src/actions';
import { IRootState } from 'src/reducers';
import Dropzone from 'react-dropzone';
import useFetch from 'use-http';
import { SelectItem } from 'src/models/common/SelectItem';
import Button from 'src/components/controls/forms/redesign/FormButton';
import Leasehold from 'src/models/leasehold/Leasehold';
import * as Constants from 'src/components/common/Constants';
import FileTooLargeDialog from 'src/components/project/sections/LeasingComponents/FileTooLargeDialog';
import { DocumentTypes } from 'src/models/DocumentTypes';
import ReportFileDetails from 'src/models/ReportFileDetails';
import LoadingSpinner from '../common/LoadingSpinner';

// TODO: Replace contract hold id with contract lookup id for all types.
interface IDocumentManagementOwnProps {
    filesUploaded: () => void;
    context: string;
    leasehold?: Leasehold;
    contractLookupId?: number;
    filesToUpload?: File[];
    tractMORfiles?: ReportFileDetails[];
    title?: string;
    documentTypes?: number[];

    clientNumber?: number;
    projectNumber?: number;
    fieldNumber?: number;
    tractNumber?: number;
    horizonNumber?: number;
    userId?: number;
}

type DocumentManagementProps = IDocumentManagementOwnProps;

const DocumentManagement = (props: DocumentManagementProps) => {
    const appContext = useSelector((state: IRootState) => state.appContext);
    const { get, post, response } = useFetch();
    const dispatch = useDispatch();
    const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
    const [downloadedFileBlob, setDownloadedFileBlob] = useState<BlobPart>(undefined);
    const [reportFilesToFetch, setReportFilesToFetch] = useState<ReportFileDetails[]>(undefined);
    const [loadingFiles, setLoadingFiles] = useState<boolean>(false);
    const [errorLoading, setErrorLoading] = useState<boolean>(false);
    const [hasExpirationDate, setHasExpirationDate] = useState<boolean>(false);
    const [completedRows, setCompletedRows] = useState<number>(0);

    useEffect(() => {
        const contractLookupId = (props.contractLookupId !== null && props.contractLookupId !== undefined) ? props.contractLookupId : 0;
        if (reportFilesToFetch?.length > 0) {
            getReportFile(reportFilesToFetch[0].reportFileType, contractLookupId, reportFilesToFetch[0].tractType, reportFilesToFetch[0].globalId);
            reportFilesToFetch.shift();
            let newReportFilesToFetch = [...reportFilesToFetch];
            setReportFilesToFetch(newReportFilesToFetch);
        }
    }, [downloadedFileBlob]);

    useEffect(() => {
        if (props?.tractMORfiles?.length > 0) {
            downloadTractMORsAndUploadToAWSS3(props.tractMORfiles);
        }
    }, [props.tractMORfiles]);

    useEffect(() => {
        if (filesToUpload?.length > 0) {
            uploadFilesFromReportServer(filesToUpload);
        }
    }, [filesToUpload]);

    useEffect(() => {
        if (props?.filesToUpload?.length > 0) {
            setFilesToUpload(props.filesToUpload);
        }
    }, [props.filesToUpload]);

    const upload = async (formData: FormData, contractTypeId: number = null, rowStart = 0, parentOwnershipId = 0) => {
        let result;

        switch (props.context) {
            case 'lease':
            case 'rightOfWay':
                await post(
                    `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/s3bucket/agreements/${props.context}/${appContext.leaseId}`,
                    formData
                );
                break;
            case 'mineral tract':
                // change to mineral tract path
                await post(
                    `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.fieldNumber}/tracts/${appContext.tractNumber}/horizons/${appContext.horizonNumber}/s3bucket`,
                    formData
                );
                break;
            case 'surface tract':
                // change to surface tract path
                await post(
                    `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.fieldNumber}/surface-tracts/${appContext.surfaceTractNumber}/s3bucket`,
                    formData
                );
                break;
            case 'leasehold':
                // change to leasehold path
                await post(
                    `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/tracts/${appContext.tractNumber}/horizons/${appContext.horizonNumber}/leasehold/${props.leasehold?.id}/s3bucket`,
                    formData
                );
                break;
            case 'gis':
                if (props.clientNumber && props.projectNumber && props.fieldNumber && props.tractNumber && props.horizonNumber) {
                    await post(
                        `/v1.0/clients/${props.clientNumber}/projects/${props.projectNumber}/fields/${props.fieldNumber}/tracts/${props.tractNumber}/horizons/${props.horizonNumber}/s3bucket`,
                        formData
                    );
                } else {
                    props.filesUploaded();
                }
                break;
            case 'contract hold':
                await post(
                    `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.fieldNumber}/contract-holds/${props.contractLookupId}/s3bucket`,
                    formData
                );
                break;
            case 'uploader':
                setLoadingFiles(true);
                result = await post(
                    `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/upload/${rowStart}/parent-owner/${parentOwnershipId}`,
                    formData
                );
                break;
            case 'user':
                setLoadingFiles(true);
                await post(
                    `/v1.0/s3bucket/user/${props.userId}`,
                    formData
                );
                break;
            case 'scorecard':
                setLoadingFiles(true);
                await post(
                    `/v1.0/s3bucket/user/${props.userId}`,
                    formData
                );
                break;
            case 'document template':
                setLoadingFiles(true);
                await post(
                    `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/s3bucket/template/${contractTypeId}`,
                    formData
                );
                break;
            case 'receipt':
                setLoadingFiles(true);
                await post(
                    `/v1.0/s3bucket/AccountsPayable/creditcard/transaction/${props.contractLookupId}`,
                    formData
                );
                break;
            default:
                break;
        }

        if (response.ok && !!result && !result.endOfDocument) {
            setCompletedRows(result.rowCount);
            upload(formData, contractTypeId, result.rowCount, result.parentOwnershipId);
        } else if (response.ok) {         
            setLoadingFiles(false);
            props.filesUploaded();
        } else {
            setLoadingFiles(false);
            setErrorLoading(true);
        }
    };

    const showFileTooLargeDialog = () => {
        dispatch(actions.layout.showDialog(DialogList.FILE_TOO_LARGE_TO_UPLOAD));
    };

    const uploadFileCanceled = () => {
        dispatch(actions.layout.hideDialog());
    };

    const fieldTypeValid = (fileType: string) => {
        let validFileTypes = new Array<string>();
        switch (props.context) {
            case 'document template':
                validFileTypes.push('docx');
                validFileTypes.push('doc');
                break;
            default:
                return true;
        }
        return validFileTypes.includes(fileType.toLowerCase());
    };

    const downloadTractMORsAndUploadToAWSS3 = (tractMORFiles: ReportFileDetails[]) => {
        let reportFiles = new Array<ReportFileDetails>();
        for (let fileIndex = 0; fileIndex < tractMORFiles.length; fileIndex++) {
            let reportFile: ReportFileDetails = { reportFileType: Constants.refreshMORReportFileType, tractType: tractMORFiles[fileIndex].tractType, globalId: tractMORFiles[fileIndex].globalId };
            reportFiles.push(reportFile);
        }
        const contractLookupId = (props.contractLookupId !== null && props.contractLookupId !== undefined) ? props.contractLookupId : 0;
        if (reportFiles.length > 0) {
            getReportFile(reportFiles[0].reportFileType, contractLookupId, reportFiles[0].tractType, reportFiles[0].globalId);
            reportFiles.shift();
        }
        setReportFilesToFetch(reportFiles);
    };

    const getReportFile = async (morOutputType: number, contractLookupId: number, tractPrefix: string, globalId: string) => {

        await get(`v1.0/reports/GetReport/${morOutputType}/globalId/${globalId}/contractLookupId/${contractLookupId}/leaseNumber/0`);
        let fileBlob: BlobPart = undefined;
        if (response.ok) {
            await response.blob().then((blob) => {
                fileBlob = blob;
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.getElementById('refreshMORReport') as HTMLAnchorElement;
                a.href = blobUrl;
                a.download = morOutputType === 1 ? `${tractPrefix}-${globalId}-MOR.xlsx` : `${tractPrefix}-${globalId}-MOR.pdf`;
                a.click();
                window.URL.revokeObjectURL(blobUrl);
            });
        }
        console.log('Would upload to S3');
        let uploadFile = new File([fileBlob], morOutputType === 1 ? `${tractPrefix}-${globalId}-MOR.xlsx` : `${tractPrefix}-${globalId}-MOR.pdf`);
        setFilesToUpload([uploadFile]);
        setDownloadedFileBlob(fileBlob);
    };

    const uploadFilesFromReportServer = (selectedFiles: File[]) => {
        selectedFiles.map((file) => {
            let docType = 1;
            if (file.name.toLowerCase().search('mor') > 0) {
                docType = 8;
            } else if (file.name.toLowerCase().search('lpr') > 0) {
                docType = 1;
            }
            const docTypeString = DocumentTypes[docType].name;
            const note = docTypeString + ' automatically uploaded from report server.';
            const formData = new FormData();
            formData.append('fileObject', file);
            formData.append('fileName', file.name);
            formData.append('fileType', file.name.split('.').pop());
            formData.append('note', note || 'NA');
            formData.append('docType', docType.toString() || 'NA');

            if (file.size > Constants.MaxUploadFileSizeBytes) {
                showFileTooLargeDialog();
            } else {
                upload(formData);
            }
        });
    };

    const takeFilesFromDropzone = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, selectedFiles: File[]) => {
        e.preventDefault();
        selectedFiles.map((file, index) => {
            console.log('doctype', document.getElementById(`docType${index}`));
            let contractTypeId = null;
            if (props.context === 'document template') {
                let contractType = (document.getElementById(`contractType${index}`) as HTMLInputElement)?.value;
                contractTypeId = contractType ? parseInt(contractType, 10) : null;
            }
            const docType = (document.getElementById(`docType${index}`) as HTMLInputElement)?.value;
            const note = (document.getElementById(`note${index}`) as HTMLInputElement)?.value;
            const expirationDate = (document.getElementById(`expirationDate${index}`) as HTMLInputElement)?.value;
            const fileType = file.name.split('.').pop();
            const formData = new FormData();
            formData.append('fileObject', file);
            formData.append('fileName', file.name);
            formData.append('fileType', fileType);
            formData.append('note', note || 'NA');
            formData.append('docType', docType || 'NA');
            formData.append('expirationDate', expirationDate || null);

            if (props.context !== 'uploader' && props.context !== 'document template') {
                (document.getElementById(`docType${index}`) as HTMLInputElement).value = undefined;
                (document.getElementById(`note${index}`) as HTMLInputElement).value = '';
            }

            if (file.size > Constants.MaxUploadFileSizeBytes) {
                showFileTooLargeDialog();
            } else if (!fieldTypeValid(fileType)) {
                window.alert('Invalid file type for the current context: ' + props.context);
            } else {
                upload(formData, contractTypeId);
            }
        });
    };

    const documentTypeChanged = (e: ChangeEvent<HTMLSelectElement>) => {
        if (e.currentTarget.selectedIndex >= 21) {
            setHasExpirationDate(true);
        } else {
            setHasExpirationDate(false);
        }
    };

    const docTypeDropDown = (docIndex: number) => {
        return (
            <div className="mb-1">
                <label style={{ display: 'inline-block', width: '20%' }}>Document Type:</label>
                <select required={true} style={{ border: '1px solid black', width: '30%' }} name={`docType${docIndex}`} id={`docType${docIndex}`} onChange={(e) => documentTypeChanged(e)}>
                    {props.context !== 'scorecard' && props.context !== 'receipt' && props.context !== 'document template' && DocumentTypes.map((docType: SelectItem, index) => {
                        return (
                            <option key={index} value={docType.id}>
                                {docType.name}
                            </option>
                        );
                    })}
                    {props.context === 'scorecard' && DocumentTypes.filter(d => d.id === 24).map((docType: SelectItem, index) => {
                        return (
                            <option key={index} value={docType.id}>
                                {docType.name}
                            </option>
                        );
                    })}
                    {props.context === 'receipt' && DocumentTypes.filter(d => d.id === 25).map((docType: SelectItem, index) => {
                        return (
                            <option key={index} value={docType.id}>
                                {docType.name}
                            </option>
                        );
                    })}
                    {props.context === 'document template' && Object.values(Constants.documentTemplateTypes).map((templateType, index) => {
                        return (
                            <option key={index} value={templateType}>
                                {templateType}
                            </option>
                        );
                    })}
                </select>
            </div>
        );
    };

    const contractTypeDropDown = (docIndex: number) => {
        return (
            <div className="mb-1">
                <label style={{ display: 'inline-block', width: '20%' }}>Document Type:</label>
                <select required={true} style={{ border: '1px solid black', width: '30%' }} name={`contractType${docIndex}`} id={`contractType${docIndex}`}>
                    {Object.values(Constants.leaseTypes).map((leaseType, index) => {
                        return (
                            <option key={index} value={index + 1}>
                                {leaseType}
                            </option>
                        );
                    })}
                </select>
            </div>
        );
    };

    return (
        <>
            <Dialog
                title={props.title ? props.title : `Entity Info: ${appContext.clientNumber || 0}.${appContext.projectNumber || 0}`}
                dialogName={DialogList.DOCUMENT_MANAGEMENT}
            >
                <div style={{ width: '70rem' }}>
                    <div>
                        <div style={{ margin: '1rem' }}>
                            <Dropzone>
                                {({ getRootProps, getInputProps, acceptedFiles }) => (
                                    <>
                                        <section className="container">
                                            <div {...getRootProps({ className: 'dropzone' })}>
                                                <input {...getInputProps()} />
                                                <p>Drag 'n' drop some files here, or click to select files</p>
                                            </div>

                                            <div>
                                                <h4>Files ready to be uploaded</h4>
                                                {acceptedFiles &&
                                                    acceptedFiles.map((f, index) => {
                                                        return (
                                                            <div key={index}>
                                                                <div className="mb-1">
                                                                    <label style={{ display: 'inline-block', width: '20%' }}>File Name:</label>
                                                                    <span>{f.name}</span>
                                                                </div>
                                                                {props.context !== 'uploader' && props.context !== 'document template' && (
                                                                    <>
                                                                        {/* <input type="text" id={`docType${index}`} className="mr-2"/> */}
                                                                        {/* <input type="text" id={`note${index}`} /> */}
                                                                        {/* <FormTextBox name={`docType${index}`} label="Doc Type" className="mr-2" /> */}
                                                                        {/* <FormSelect name={`docType${index}`} items={docTypes} className="mr-2" label="Doc Type"/> */}
                                                                        {/* <FormTextBox name={`note${index}`} label="Notes" /> */}
                                                                        {docTypeDropDown(index)}
                                                                        <div className="mb-1">
                                                                            <label style={{ display: 'inline-block', width: '20%', verticalAlign: 'top' }}>
                                                                                Notes:
                                                                            </label>
                                                                            <textarea
                                                                                required={true}
                                                                                style={{ width: '30%' }}
                                                                                name={`note${index}`}
                                                                                id={`note${index}`}
                                                                            />
                                                                        </div>
                                                                        {hasExpirationDate && <div className="mb-1">
                                                                            <label style={{ display: 'inline-block', width: '20%', verticalAlign: 'top' }}>
                                                                                Expiration Date:
                                                                            </label>
                                                                            <input
                                                                                type="date"
                                                                                style={{width: '30%'}}
                                                                                name={`expirationDate${index}`}
                                                                                id={`expirationDate${index}`}
                                                                            />
                                                                        </div>}
                                                                    </>
                                                                )}
                                                                {props.context === 'document template' && (
                                                                    <>
                                                                        {docTypeDropDown(index)}
                                                                        {contractTypeDropDown(index)}
                                                                    </>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </section>
                                        <div className="mt-2">
                                            {/* <button onClick={(e) => takeFilesFromDropzone(e, acceptedFiles)}>Upload</button> */}
                                            <Button
                                                disabled={acceptedFiles.length < 1 || loadingFiles}
                                                text="Upload"
                                                onClick={(e) => takeFilesFromDropzone(e, acceptedFiles)}
                                                intent="primary"
                                            />
                                        </div>
                                    </>
                                )}
                            </Dropzone>
                            {/* <input type="file" onChange={onFileChange} /> */}
                            {/* <button disabled={!fileToUpload} onClick={uploadFiles}>Upload</button> */}
                        </div>
                    </div>
                    {loadingFiles && (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span>Rows Processed: {completedRows}</span>
                            <span>This process may take a while.</span>
                            <LoadingSpinner />
                        </div>
                    )}
                    {errorLoading && (
                        <div>
                            <span style={{ color: 'red' }}>Error uploading document</span>
                        </div>
                    )}
                </div>
            </Dialog>
            <FileTooLargeDialog acknowledge={uploadFileCanceled} />
        </>
    );
};

export default DocumentManagement;



// WEBPACK FOOTER //
// ./src/components/layout/DocumentManagement.tsx